import ms from 'ms';
import lodashXor from 'lodash/fp/xor';

import {
  USER_ROLE_ANONYMOUS,
} from 'common/userRoles';

import {
  isApartmentLead,
  isUnreasonableApartmentLead,
} from 'common/leadTypeHelpers';
import {
  actionResetFeedCategoryList,
  actionFeedCategoryLoadlistings,
} from 'client/helpers/feedCategoryHelpers';
import {
  getCurrentUserRenterProfile,
} from 'common/householdHelpers';
import {
  FEED_CATEGORY_QUALIFIED_TO_RENT,
  FEED_CATEGORY_BEST_MATCHES,
} from 'common/feedCategoryConstants';
import {
  SR_API_ACTION_ASSIGN_LEAD_APARTMENT_LOCATOR,
  SR_API_ACTION_CANCEL_LEAD_APARTMENT_LOCATOR_DELAYED_ASSIGNMENT,
} from 'api/actionTypes';
import {
  APT_LEAD_ASSIGNMENT_CONTEXT_UPDATED_FILTERS,
  APT_LEAD_ASSIGNMENT_DISPLAY_CONTEXT_UPDATED_FILTERS,
} from 'common/leadConstants';

import analytics from 'client/analytics';
import {
  ANALYTICS_EVENT_SEARCH_FILTERS_UPDATED,
} from 'client/analytics/eventList';

import log from 'client/lib/log';
import UserActions from 'client/actions/User';
import ListingActions from 'client/actions/Listing';

const debug = log.debug('sunroom:Search');

export default async function applyFilters({ state, apiClient }) {
  const userRoles = state.get(['currentUser', 'roles']);
  const currentUser = state.get(['currentUser']);
  const searchFilters = state.get(['search', 'filters']);
  const previousSearchFilters = state.get(['currentUser', 'searchFilters']);
  const previousViewedListings = state.get(['listingsMapView', 'viewedListings']);
  const isAnonymous = userRoles.includes(USER_ROLE_ANONYMOUS);

  debug('Appling filters', searchFilters);

  if (isAnonymous) {
    state.set(['currentUser', 'searchFilters'], searchFilters);
  }
  else {
    await UserActions.update({ state, apiClient }, { searchFilters });
    const updatedUser = state.get(['currentUser']);

    // Assign an apartment locator to this lead when they change their property type filters to only apartments.
    if (
      lodashXor(searchFilters.propertyTypes, previousSearchFilters.propertyTypes).length > 0
      || searchFilters.minRentPrice !== previousSearchFilters.minRentPrice
      || searchFilters.maxRentPrice !== previousSearchFilters.maxRentPrice
      || lodashXor(searchFilters.bedrooms, previousSearchFilters.bedrooms).length > 0
    ) {
      if (isApartmentLead(updatedUser) && !isUnreasonableApartmentLead(updatedUser)) {
        await apiClient.post(SR_API_ACTION_ASSIGN_LEAD_APARTMENT_LOCATOR, {
          userId: currentUser.id,
          assignmentContext: APT_LEAD_ASSIGNMENT_CONTEXT_UPDATED_FILTERS,
          displayContext: APT_LEAD_ASSIGNMENT_DISPLAY_CONTEXT_UPDATED_FILTERS,
          enableSmsMessage: true,
          delay: ms('3m'),
        });
      }
      else if (isApartmentLead(currentUser) && !isUnreasonableApartmentLead(currentUser)) {
        // If the user quickly changed from apartments only to homes, prevent the lead apartment locator assignment
        // to avoid assigning locators to renters that were just testing different search filter options.
        await apiClient.post(SR_API_ACTION_CANCEL_LEAD_APARTMENT_LOCATOR_DELAYED_ASSIGNMENT, {
          userId: currentUser.id,
        });
      }
    }
  }
  await ListingActions.reset({ state });

  state.set(['listingsMapView', 'mapChanged'], false);
  state.set(['listingsMapView', 'showResultsMatched'], true);
  state.set(['listingsMapView', 'viewedListings'], previousViewedListings);
  state.set(['search', 'selectedSuggestedSearchFilter'], null);
  state.commit();

  const renterProfile = getCurrentUserRenterProfile({ state });
  actionResetFeedCategoryList({ state });

  if (renterProfile) {
    actionFeedCategoryLoadlistings(
      { state, apiClient },
      { feedCategoryId: FEED_CATEGORY_QUALIFIED_TO_RENT }
    );
  }

  actionFeedCategoryLoadlistings({ state, apiClient }, { feedCategoryId: FEED_CATEGORY_BEST_MATCHES });

  analytics.track(ANALYTICS_EVENT_SEARCH_FILTERS_UPDATED, searchFilters);
}
